import { useEffect, useMemo, useState } from "react";
import "intersection-observer";
import config from "config";
import Cookies from "universal-cookie";
import CookiesJS from "js-cookie";
import FullScreenMessage from "../../Reusable/FullScreenMessage/FullScreenMessage.js";
import Image from "../../Elements/Image/Image.tsx";
import ModalManager from "../ModalManager/ModalManager.js";
import PageContent from "./PageContent.tsx";
import setAccessabilityButtonDisplay from "./helpers/setAccessabilityButtonDisplay.ts";
import useCustomLoginErrorHandler from "hooks/useCustomLoginErrorHandler.js";
import Forbidden from "components/Pages/Forbidden";
import useCustomDomainRedirect from "hooks/useCustomDomainRedirect.ts";
import checkCurrDomainIsOrgDomain from "helpers/checkCurrDomainIsOrgDomain.ts";
import { detectOldBrowser } from "../../../utils/gridHelpers.js";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { usePicker } from "contexts/pickerContext/pickerContext";
import { useUser } from "../../../contexts/userContext/userContext.js";

import "./Page.scss";
import { CustomDomainPagesE } from "models/enums.ts";
import { GoogleOAuthProvider } from "@react-oauth/google";

const cookies = new Cookies();

const isLocalHost = window.location.origin.includes("3000");
const isCustomDomain = checkCurrDomainIsOrgDomain();
const MIN_PATH_LENGTH = 4;

/**
 * Page wrapper, everything here is on every page
 */

const NotSupportedPage = () => {
  const val = detectOldBrowser();
  return (
    <FullScreenMessage
      message={{
        isShow: true,
        data: {
          title: `Your browser is not supported anymore (${JSON.stringify(val)
            .replace("{", "")
            .replace("}", "")
            .replaceAll('"', "")})`,
          content:
            "We are sorry but in order to continue using our service, you will need to upgrade your browser to a newer one.",
          image: { src: "robot", ext: "png" },
        },
      }}
      isShowOnStart={true}
    >
      <div className="full-screen-message-content">
        <div className="browser-group">
          <a
            href="https://www.google.com/intl/en/chrome/"
            target="_blank"
            rel="noreferrer"
          >
            <Image name="chrome" ext="png" />
          </a>
          <a
            href="https://www.microsoft.com/edge"
            target="_blank"
            rel="noreferrer"
          >
            <Image name="edge" ext="png" />
          </a>
          <a
            href="https://www.mozilla.org/firefox/new/"
            target="_blank"
            rel="noreferrer"
          >
            <Image name="firefox" ext="png" />
          </a>
          <a
            href="https://support.apple.com/downloads/safari"
            target="_blank"
            rel="noreferrer"
          >
            <Image name="safari" ext="png" />
          </a>
        </div>
      </div>
    </FullScreenMessage>
  );
};

const Page = ({ children }) => {
  const {
    initBasicSettings,
    isBasicSettingsInitialized,
    fetchRecaptchaRequired,
    getBasicSettingsData,
    getLanguage,
  } = useContent();
  const lang = getLanguage();
  const {
    authenticateAfterMvc,
    fetchIsraelUser,
    getUserData,
    isLoggedIn,
    getIsPendingLogin,
    setIsPendingLogin,
    logout,
  } = useUser();
  const userData = getUserData();
  const [publicClientApplication, setPublicClientApplication] = useState(null);
  const { getIFrameLink } = usePicker();

  const { checkRedirect } = useCustomDomainRedirect();

  const userBucketName = userData?.BucketName;

  useEffect(() => {
    if (
      checkCurrDomainIsOrgDomain() &&
      userData?.IsConfirm &&
      userBucketName !== window.location.origin &&
      !window.location.pathname.includes("sub-user-complete") &&
      !window.location.pathname.includes("invite")
    ) {
      logout();
    }
  }, [userBucketName, userData?.IsConfirm]);

  useEffect(() => {
    if (!getIsPendingLogin()) {
      checkRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomDomain, isLoggedIn(), getIsPendingLogin()]);

  // usePreventZoomMobile();

  const handleCustomError = useCustomLoginErrorHandler();

  useEffect(() => {
    // Hide the accessability button on the picker pages which are displayed in an iframe
    if (getIFrameLink()) {
      setAccessabilityButtonDisplay("none");
    } else {
      setAccessabilityButtonDisplay("block");
      // Display an accessability icon from the right side for the inbox pages
      // and from the left side for the other pages
      if (window.location.pathname.includes("inbox")) {
        import("./InboxStyles.scss");
      } else if (window.location.pathname.includes("gallery")) {
        import("./GalleryStyles.scss");
      } else {
        import("./PageStyles.scss");
      }
    }
  }, [getIFrameLink()]);

  // Microsoft SSO config
  useEffect(() => {
    if (getBasicSettingsData()?.microsoftClientId) {
      const config = {
        auth: {
          clientId: getBasicSettingsData()?.microsoftClientId,
          redirectUri: window.location.origin,
        },
      };

      setPublicClientApplication(new PublicClientApplication(config));
    }
  }, [getBasicSettingsData()?.microsoftClientId]);

  useEffect(() => {
    if (publicClientApplication) {
      publicClientApplication?.initialize();
    }
  }, [publicClientApplication]);

  useEffect(() => {
    fetchRecaptchaRequired();
    if (!cookies.get(config.api.jumbomailApiAuthCookie)) {
      fetchIsraelUser();
    }
  }, []);

  const reactAuthCookie = CookiesJS.get(config.api.jumbomailApiAuthCookie);

  const authAfterMvc = async () => {
    if (!detectOldBrowser()) {
      if (reactAuthCookie && isBasicSettingsInitialized()) {
        const res = await authenticateAfterMvc();
        // detecting a login method error
        if (res?.status === "error") {
          handleCustomError(res.data);
        }
      }
      if (!reactAuthCookie) {
        setIsPendingLogin(false);
      }
      //fetching basic settings
      !isBasicSettingsInitialized() && initBasicSettings();
    }
  };

  useEffect(() => {
    // redirect to full url
    if (
      window.location.pathname.length <= MIN_PATH_LENGTH &&
      !getIsPendingLogin()
    ) {
      window.location.href =
        userData || !isCustomDomain ? `/${lang}/home` : `/en/login`;
    }
  }, [userData, getIsPendingLogin()]);

  useEffect(() => {
    authAfterMvc();
  }, [reactAuthCookie, isBasicSettingsInitialized()]);

  useEffect(() => {
    // Don't redirect if we're in the jumboforms-pages - sitemap page
    if (window.location.pathname === "/jumboforms-pages") {
      return;
    }
    if (window.location.pathname.startsWith("/j/")) {
      window.location.href = window.location.href.replace("/j/", "/en/j/");
      return;
    }

    // redirect to full url
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    // Check if we have at least language and page segments
    // But don't redirect if we're in the middle of a purchase flow
    if (
      pathSegments.length < 2 &&
      !window.location.pathname.includes("purchase")
    ) {
      const user = localStorage.getItem("user");
      window.location.href =
        user || !isCustomDomain ? `/${lang}/home` : `/en/login`;
    }
  }, [lang]);

  // Don't allow to see restricted pages for not verified users under the custom domain
  useEffect(() => {
    if (
      userData?.UserId &&
      !userData?.IsConfirm &&
      checkCurrDomainIsOrgDomain()
    ) {
      const allowedPages = [
        CustomDomainPagesE.GALLERY,
        CustomDomainPagesE.COLLECTION,
        CustomDomainPagesE.PAGE,
        CustomDomainPagesE.INVITE,
        CustomDomainPagesE.SUB_USER_COMPLETE,
        CustomDomainPagesE.REGISTRATION_COMPLETE,
        CustomDomainPagesE.USER_CONFIRMATION,
      ];
      const currPage = window.location.pathname.split("/")[2];

      if (!allowedPages.includes(currPage)) {
        window.location.href = `/${lang}/registration-complete/incomplete`;
      }
    }
  }, [userData?.IsConfirm, userData?.UserId, lang]);

  // if (window.location.pathname.includes("/forbidden")) {
  //   return <Forbidden />;
  // }

  // if (detectOldBrowser()) {
  //   return <NotSupportedPage />;
  // }

  const googleClientId = getBasicSettingsData()?.googleClientId;

  return useMemo(() => {
    return detectOldBrowser() ? (
      <NotSupportedPage />
    ) : window.location.pathname.includes("/forbidden") ? (
      <Forbidden />
    ) : (
      <>
        {publicClientApplication && !isLocalHost ? (
          <MsalProvider instance={publicClientApplication}>
            <>
              <GoogleOAuthProvider clientId={googleClientId}>
                <PageContent
                  isBasicSettingsInitialized={isBasicSettingsInitialized()}
                  isMsalInitialized
                />
                <ModalManager />
              </GoogleOAuthProvider>
            </>
          </MsalProvider>
        ) : (
          <>
            <GoogleOAuthProvider clientId={googleClientId}>
              <PageContent
                isBasicSettingsInitialized={isBasicSettingsInitialized()}
              />
              <ModalManager />
            </GoogleOAuthProvider>
          </>
        )}
      </>
    );
  }, [getBasicSettingsData(), publicClientApplication]);
};
export default Page;
